import { useNavigate, Link } from "react-router-dom"
import { motion } from "framer-motion"
import { SwipeCards } from "../../components/SwipeCards"
import { VisaMark, MastercardMark, ElcardMark } from "../../components/PaymentBadges"
// import { FloatingAvatars } from "../../components/FloatingAvatars"
// import { LiveChat } from "../../components/LiveChat"
import {
  AcademicCapIcon,
  UsersIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  BuildingOffice2Icon,
  TagIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  StarIcon,
  BookOpenIcon,
  ShieldCheckIcon,
  BriefcaseIcon,
  SparklesIcon,
  HeartIcon,
} from "@heroicons/react/16/solid"

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
}

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
}

export const LandingPage = () => {
  const navigate = useNavigate()
  const token = localStorage.getItem("access_token")

  const handleEnter = () => {
    navigate(token ? "/courses" : "/login")
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden">

      {/* ── NAVBAR ── */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <img src="/horizontal-1.png" alt="Vkonekte" className="h-32 w-auto" />
          </div>

          <nav className="hidden md:flex items-center gap-6 text-sm text-gray-500">
            <a href="#features" className="hover:text-gray-900 transition-colors">Возможности</a>
            <a href="#how" className="hover:text-gray-900 transition-colors">Как это работает</a>
            <a href="#academy" className="hover:text-gray-900 transition-colors">Академия</a>
          </nav>

          <button
            onClick={handleEnter}
            className="group relative inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold pl-4 pr-3.5 py-2 rounded-full shadow-sm shadow-blue-200/70 ring-1 ring-blue-700/10 transition-all hover:shadow-md hover:shadow-blue-300/60 hover:-translate-y-0.5 active:translate-y-0"
          >
            Войти в панель
            <ArrowRightIcon className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/70 via-white to-white pt-16 pb-20">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-100/50 blur-3xl pointer-events-none" />
        <div className="absolute top-20 -left-20 w-80 h-80 rounded-full bg-indigo-100/40 blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">

            {/* Left: text */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="flex-1 min-w-0"
            >
              <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
                <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-blue-100 mb-6">
                  <SparklesIcon className="w-3.5 h-3.5" />
                  Платформа для специалистов маркетплейсов
                </span>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                transition={{ duration: 0.6 }}
                className="text-5xl md:text-6xl font-extrabold leading-[1.1] text-gray-900 mb-6"
              >
                Найди своих.
                <span className="text-blue-600 block mt-1">Расти вместе.</span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-gray-500 text-lg leading-relaxed mb-10 max-w-lg"
              >
                Vkonekte — сервис для специалистов Wildberries, Ozon и других маркетплейсов.
                Найди партнёров и сотрудников через мэтчинг, прокачай навыки в GS Academy
                и выстрой репутацию через отзывы и рейтинги.
              </motion.p>

              <motion.div variants={fadeUp} transition={{ duration: 0.5, delay: 0.2 }} className="flex gap-3 flex-wrap">
                <button
                  onClick={handleEnter}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-xl transition-colors shadow-lg shadow-blue-200"
                >
                  Войти в админ-панель
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </motion.div>
            </motion.div>

            {/* Right: swipe cards */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex-shrink-0 w-full md:w-auto"
            >
              <SwipeCards />
              {/* <FloatingAvatars /> */}
              {/* <LiveChat /> */}
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="border-y border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {[
              { value: "1,200+", label: "Специалистов",  icon: <UsersIcon className="w-4 h-4 text-blue-500" /> },
              { value: "50+",    label: "Курсов в GS Academy", icon: <AcademicCapIcon className="w-4 h-4 text-violet-500" /> },
              { value: "30+",    label: "Менторов",      icon: <StarIcon className="w-4 h-4 text-amber-500" /> },
              { value: "WB · Ozon", label: "Маркетплейсы", icon: <ShieldCheckIcon className="w-4 h-4 text-emerald-500" /> },
            ].map((s) => (
              <motion.div key={s.label} variants={fadeUp} transition={{ duration: 0.5 }} className="text-center">
                <div className="inline-flex items-center justify-center mb-2">{s.icon}</div>
                <p className="text-2xl font-extrabold text-gray-900">{s.value}</p>
                <p className="text-sm text-gray-400 mt-1 leading-tight">{s.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="py-24 bg-gray-50/50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} className="text-blue-600 text-xs font-bold mb-3 uppercase tracking-widest">
              Возможности платформы
            </motion.p>
            <motion.h2 variants={fadeUp} transition={{ duration: 0.5 }} className="text-3xl md:text-4xl font-bold text-gray-900">
              Всё для роста специалиста
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-400 mt-4 max-w-xl mx-auto text-sm">
              От поиска партнёра до получения сертификата — все инструменты в одном месте
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {[
              {
                icon: <HeartIcon className="w-5 h-5" />,
                color: "bg-pink-50 text-pink-600",
                title: "Мэтчинг и свайпы",
                text: "Находи партнёров и сотрудников через алгоритм подбора. Свайпай профили, получай взаимный интерес — получай «connect!».",
              },
              {
                icon: <UsersIcon className="w-5 h-5" />,
                color: "bg-violet-50 text-violet-600",
                title: "Профили специалистов",
                text: "Подробные анкеты с навыками, опытом, портфолио, рейтингом и бейджами. Сертификаты из GS Academy влияют на видимость.",
              },
              {
                icon: <BriefcaseIcon className="w-5 h-5" />,
                color: "bg-amber-50 text-amber-600",
                title: "Биржа вакансий",
                text: "Публикуй резюме и вакансии. Откликайся на проекты, отслеживай статусы откликов и управляй публикациями.",
              },
              {
                icon: <ChatBubbleLeftRightIcon className="w-5 h-5" />,
                color: "bg-emerald-50 text-emerald-600",
                title: "Чаты и коммуникации",
                text: "Текст, файлы, голосовые сообщения. Чат доступен только при взаимном мэтче. Push-уведомления и напоминания.",
              },
              {
                icon: <AcademicCapIcon className="w-5 h-5" />,
                color: "bg-blue-50 text-blue-600",
                title: "GS Academy",
                text: "Обучение в формате видео- и текстовых курсов с практическими заданиями. Сертификат после завершения курса.",
              },
              {
                icon: <BuildingOffice2Icon className="w-5 h-5" />,
                color: "bg-red-50 text-red-600",
                title: "Каталог Китая",
                text: "База контактов фабрик, байеров и поставщиков. Структурированный каталог для работы с китайским бизнесом.",
              },
              {
                icon: <StarIcon className="w-5 h-5" />,
                color: "bg-amber-50 text-amber-600",
                title: "Репутация и отзывы",
                text: "Рейтинг 1–10 на основе реального сотрудничества. Отзывы с шаблонными характеристиками и защитой от накрутки.",
              },
              {
                icon: <TagIcon className="w-5 h-5" />,
                color: "bg-teal-50 text-teal-600",
                title: "Сообщества",
                text: "Вступай в тематические сообщества (SMM, маркетинг, логистика). Свайп внутри сообщества — только релевантные специалисты.",
              },
              {
                icon: <SparklesIcon className="w-5 h-5" />,
                color: "bg-indigo-50 text-indigo-600",
                title: "ИИ-агент",
                text: "Встроенный чат-бот анализирует профиль и даёт рекомендации по улучшению портфолио, навыкам и поиску вакансий.",
              },
            ].map((f) => (
              <motion.div
                key={f.title}
                variants={fadeUp}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-2xl border border-gray-100 p-6 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50/60 transition-all"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${f.color}`}>
                  {f.icon}
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{f.title}</h4>
                <p className="text-sm text-gray-400 leading-relaxed">{f.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.p variants={fadeUp} className="text-blue-600 text-xs font-bold mb-3 uppercase tracking-widest text-center">
              Как это работает
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-center text-gray-900 mb-14">
              Просто. Быстро. Эффективно.
            </motion.h2>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  step: "01",
                  icon: <UsersIcon className="w-5 h-5 text-blue-600" />,
                  title: "Создай профиль",
                  text: "Заполни анкету, добавь навыки, опыт и портфолио.",
                },
                {
                  step: "02",
                  icon: <HeartIcon className="w-5 h-5 text-blue-600" />,
                  title: "Мэтчингуй",
                  text: "Свайпай специалистов по нише и интересам. При взаимном интересе — connect!",
                },
                {
                  step: "03",
                  icon: <ChatBubbleLeftRightIcon className="w-5 h-5 text-blue-600" />,
                  title: "Общайся",
                  text: "Переписывайся в чате, обсуждай проекты и договаривайся о сотрудничестве.",
                },
                {
                  step: "04",
                  icon: <AcademicCapIcon className="w-5 h-5 text-blue-600" />,
                  title: "Расти",
                  text: "Проходи курсы в GS Academy, получай сертификаты и повышай рейтинг.",
                },
              ].map((item, i) => (
                <motion.div
                  key={item.step}
                  variants={fadeUp}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="text-center"
                >
                  <div className="inline-flex flex-col items-center">
                    <span className="text-5xl font-black text-blue-100 leading-none mb-3">{item.step}</span>
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                      {item.icon}
                    </div>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── ACADEMY BANNER ── */}
      <section id="academy" className="py-16 bg-gradient-to-r from-indigo-50 to-blue-50 border-y border-blue-100/60">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="flex flex-col md:flex-row items-center justify-between gap-8"
          >
            <div className="max-w-xl">
              <motion.div variants={fadeUp}>
                <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                  <AcademicCapIcon className="w-3.5 h-3.5" />
                  GS Academy
                </span>
              </motion.div>
              <motion.h3 variants={fadeUp} className="text-2xl font-bold text-gray-900 mb-3">
                Обучай. Сертифицируй. Выделяйся.
              </motion.h3>
              <motion.p variants={fadeUp} className="text-gray-500 text-sm leading-relaxed">
                GS Academy — встроенная система обучения внутри платформы. Видеоуроки,
                практические задания и сертификаты, которые напрямую влияют на твой рейтинг
                и видимость в поиске специалистов.
              </motion.p>
              <motion.div variants={fadeUp} className="flex gap-3 mt-5 flex-wrap">
                {["Видеоуроки", "Практика", "Сертификаты", "Бейдж «Проверен GS»"].map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1 bg-white border border-blue-100 text-blue-700 text-xs font-medium px-3 py-1.5 rounded-full">
                    <CheckCircleIcon className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </motion.div>
            </div>
            <motion.div variants={fadeUp} className="flex-shrink-0">
              <div className="w-64 bg-white rounded-2xl border border-blue-100 shadow-xl shadow-blue-100/40 p-5 space-y-3">
                <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                    <BookOpenIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Маркетинг WB</p>
                    <p className="text-xs text-gray-400">12 уроков · 3 ч</p>
                  </div>
                </div>
                {[70, 100, 40].map((p, i) => (
                  <div key={i} className="space-y-1">
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>Урок {i + 1}</span>
                      <span>{p}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${p === 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${p}%` }} />
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-1">
                  <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-gray-500">Сертификат выдан</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 rounded-full bg-white blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="max-w-6xl mx-auto px-6 text-center relative"
        >
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-white mb-4">
            Управляй платформой
          </motion.h2>
          <motion.p variants={fadeUp} className="text-blue-100 mb-8 max-w-md mx-auto text-sm">
            Войди в административную панель Vkonekte и управляй курсами, пользователями и запросами.
          </motion.p>
          <motion.button
            variants={fadeUp}
            onClick={handleEnter}
            className="inline-flex items-center gap-2 bg-white text-blue-600 font-bold px-8 py-3.5 rounded-xl hover:bg-blue-50 transition-colors shadow-lg"
          >
            Войти в панель
            <ArrowRightIcon className="w-4 h-4" />
          </motion.button>
        </motion.div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-gray-950 text-gray-500 pt-14 pb-8">
        <div className="max-w-6xl mx-auto px-6">

          {/* 3-column grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 pb-10">

            {/* Column 1: Brand */}
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                <img src="/icon-2.png" alt="Vkonekte" className="h-9 w-auto brightness-0 invert opacity-70" />
                <span className="text-gray-500 text-sm font-medium">Vkonekte</span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed max-w-[220px] mb-5">
                Платформа для специалистов маркетплейсов: мэтчинг, вакансии и обучение в GS Academy.
              </p>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5 text-gray-500">
                  <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500" />WB · Ozon
                </span>
                <span className="flex items-center gap-1.5 text-gray-500">
                  <AcademicCapIcon className="w-3.5 h-3.5 text-blue-500" />GS Academy
                </span>
              </div>
            </div>

            {/* Column 2: Legal */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Документы</p>
              <nav className="flex flex-col gap-2.5 text-sm text-gray-500">
                <Link to="/public-offer" className="hover:text-gray-300 transition-colors w-fit">Публичная оферта</Link>
                <Link to="/refund-policy" className="hover:text-gray-300 transition-colors w-fit">Правила возврата</Link>
                <Link to="/card-payment" className="hover:text-gray-300 transition-colors w-fit">Оплата банковской картой</Link>
                <Link to="/privacy-policy" className="hover:text-gray-300 transition-colors w-fit">Политика конфиденциальности</Link>
                <Link to="/terms-of-use" className="hover:text-gray-300 transition-colors w-fit">Условия использования</Link>
                <Link to="/support" className="hover:text-gray-300 transition-colors w-fit">Поддержка</Link>
              </nav>
            </div>

            {/* Column 3: Payment */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Способы оплаты</p>
              <div className="flex items-center gap-2.5 flex-wrap">
                <VisaMark />
                <MastercardMark />
                <ElcardMark />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-600">© {new Date().getFullYear()} Vkonekte · Все права защищены</p>
            <p className="text-xs text-gray-700">Admin Panel</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
